import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Puzzle, Loader2, RefreshCw, LogOut, Inbox, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Field = ({ label, value }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-700">{label}</p>
    <p className="mt-0.5 text-sm font-semibold text-slate-800 whitespace-pre-wrap">{value || "—"}</p>
  </div>
);

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("ptop_admin") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [fetching, setFetching] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("ptop_admin", res.data.token);
      setToken(res.data.token);
    } catch {
      setError("Incorrect password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [inq, con] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setInquiries(inq.data.inquiries);
      setContacts(con.data.contacts);
    } catch {
      sessionStorage.removeItem("ptop_admin");
      setToken("");
    } finally {
      setFetching(false);
    }
  }, [token]);

  useEffect(() => {
    document.title = "Admin Inbox | Pieces to Our Puzzle Childcare";
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-amber-900/10 bg-white/85 p-8 shadow-2xl shadow-deepteal/10"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-deepteal text-amber-200">
            <Puzzle className="h-6 w-6" />
          </div>
          <h1 className="mt-5 font-serif text-2xl text-deepteal-dark">Admin Inbox</h1>
          <p className="mt-1 text-xs text-slate-500">Pieces to Our Puzzle Childcare — authorized access only.</p>
          <label htmlFor="admin-pass" className="mt-6 mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-deepteal">
            Password
          </label>
          <input
            id="admin-pass"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-amber-900/15 bg-white px-4 py-3 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"
            placeholder="Enter admin password"
          />
          {error && <p data-testid="admin-login-error" className="mt-2 text-xs font-semibold text-red-600">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-bold text-amber-50 transition-all hover:bg-deepteal disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>
          <a data-testid="admin-back-home" href="/" className="mt-4 block text-center text-xs font-semibold text-deepteal hover:text-terracotta">
            ← Back to website
          </a>
        </form>
      </main>
    );
  }

  const items = tab === "inquiries" ? inquiries : contacts;

  return (
    <main className="min-h-screen bg-sand">
      <div className="border-b border-amber-900/10 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-deepteal text-amber-200">
              <Puzzle className="h-5 w-5" />
            </div>
            <div>
              <p className="font-serif text-lg font-semibold text-deepteal-dark">Admin Inbox</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-700">Pieces to Our Puzzle Childcare</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-deepteal/20 px-4 py-2 text-xs font-bold text-deepteal hover:border-terracotta hover:text-terracotta transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => { sessionStorage.removeItem("ptop_admin"); setToken(""); }}
              className="inline-flex items-center gap-2 rounded-full bg-deepteal px-4 py-2 text-xs font-bold text-amber-50 hover:bg-terracotta transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex gap-2" role="tablist">
          <button
            data-testid="admin-tab-inquiries"
            role="tab"
            aria-selected={tab === "inquiries"}
            onClick={() => setTab("inquiries")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "inquiries" ? "bg-terracotta text-amber-50" : "bg-white/70 text-slate-600 hover:text-terracotta"
            }`}
          >
            <Inbox className="h-4 w-4" /> Enrollment Inquiries ({inquiries.length})
          </button>
          <button
            data-testid="admin-tab-contacts"
            role="tab"
            aria-selected={tab === "contacts"}
            onClick={() => setTab("contacts")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "contacts" ? "bg-terracotta text-amber-50" : "bg-white/70 text-slate-600 hover:text-terracotta"
            }`}
          >
            <Mail className="h-4 w-4" /> Contact Messages ({contacts.length})
          </button>
        </div>

        <div data-testid="admin-inbox-table" className="mt-6 space-y-4">
          {fetching && items.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-deepteal">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div data-testid="admin-inbox-empty" className="rounded-3xl border border-dashed border-amber-900/20 bg-white/60 py-20 text-center">
              <p className="font-serif text-xl text-deepteal-dark">No {tab === "inquiries" ? "enrollment inquiries" : "messages"} yet</p>
              <p className="mt-1 text-sm text-slate-500">New submissions will appear here automatically.</p>
            </div>
          ) : (
            items.map((item) => (
              <article
                key={item.id}
                data-testid={`admin-item-${item.id}`}
                className="rounded-3xl border border-amber-900/10 bg-white/85 p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-900/10 pb-3">
                  <p className="font-serif text-lg font-semibold text-deepteal-dark">
                    {tab === "inquiries" ? item.parent_name : item.name}
                  </p>
                  <p className="font-mono text-[11px] text-slate-500">{fmt(item.created_at)}</p>
                </div>
                {tab === "inquiries" ? (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label="Phone" value={item.phone} />
                    <Field label="Email" value={item.email} />
                    <Field label="Child's Age(s)" value={item.child_age} />
                    <Field label="Children" value={item.num_children} />
                    <Field label="Start Date" value={item.start_date} />
                    <Field label="Days Needed" value={(item.days_needed || []).join(", ")} />
                    <Field label="Schedule" value={item.preferred_schedule} />
                    <Field label="Scholarship" value={item.scholarship} />
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Field label="Message" value={item.message} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label="Email" value={item.email} />
                    <Field label="Phone" value={item.phone} />
                    <Field label="Topic" value={item.topic} />
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Field label="Message" value={item.message} />
                    </div>
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>
    </main>
  );
};

export default Admin;
